/**
 * Marketing/analytics helpers for the toast.ninja website.
 *
 * Centralizes all third-party tracking IDs and exposes small, side-effect-free
 * helpers used by pages and components. Global script injection itself happens
 * in `gatsby-ssr.js` via `setHeadComponents` so that tags are present on the
 * very first paint and don't depend on a Helmet roundtrip.
 */

export const GOOGLE_ADS_ID = process.env.GATSBY_GOOGLE_ADS_ID || 'AW-306983406'
export const GOOGLE_TAG_MANAGER_ID = process.env.GATSBY_GTM_ID || 'GTM-5GHLHLR'

/**
 * The Google Ads conversion label fired when a visitor signs up for the
 * Slack app on /onboarding/github (the dedicated post-install landing page).
 *
 * Format expected by gtag: `<AW id>/<conversion label>`.
 */
export const ADD_TO_SLACK_CONVERSION =
	process.env.GATSBY_ADD_TO_SLACK_CONVERSION || `${ GOOGLE_ADS_ID }/tkOwCOqag_QCEO7jsJIB`

/**
 * Analytics is disabled in development by default so local builds don't pollute
 * production Google Ads dashboards. Set `GATSBY_ENABLE_ANALYTICS_DEV=true` to
 * opt in while debugging tag firing locally.
 */
export const ANALYTICS_ENABLED =
	process.env.NODE_ENV === 'production' || process.env.GATSBY_ENABLE_ANALYTICS_DEV === 'true'

/**
 * Returns true when running in the browser (not during Gatsby SSR).
 * Helpers that touch `window` should bail out early via this guard.
 */
export const isBrowser = () => typeof window !== 'undefined'

/**
 * Pushes a `gtag(...)` call onto the dataLayer if gtag has been initialized.
 *
 * Uses the global `window.gtag` shim that the Google Ads / GTM bootstrap
 * scripts install. Safe to call at any time: if the bootstrap hasn't loaded
 * yet (or analytics is disabled) the call becomes a no-op.
 */
export const gtag = (...args) => {
	if (!ANALYTICS_ENABLED || !isBrowser()) return
	if (typeof window.gtag !== 'function') return
	window.gtag(...args)
}

/**
 * Fires the "Add to Slack" Google Ads conversion event.
 *
 * Should be called at the moment the user actually completes the conversion
 * step (e.g. lands on /onboarding/github after authorizing the Slack app),
 * not on every page load.
 */
export const trackAddToSlackConversion = () => {
	gtag('event', 'conversion', { send_to: ADD_TO_SLACK_CONVERSION })
}

/**
 * Fires a generic "Add to Slack" click event so we can measure how often the
 * marketing CTA is clicked, independent of the Google Ads conversion goal.
 *
 * The CTA is an `<a href="https://slack.com/...">`, so a normal click leaves
 * the page immediately and would routinely cancel the in-flight gtag beacon.
 * To make the event reliable we:
 *   1. preventDefault on the click,
 *   2. ask gtag to call us back when it has flushed the request,
 *   3. fall back to a 500 ms timeout in case gtag never resolves
 *      (e.g. analytics disabled, blocked by adblock, or already navigated).
 *
 * The returned object is meant to be spread directly onto an `<a>` element:
 *   <a href={url} {...buildAddToSlackClickHandler(url, location)}>...
 */
export const trackAddToSlackClick = (location = 'unknown', onComplete) => {
	const fired = { done: false }
	const finish = () => {
		if (fired.done) return
		fired.done = true
		if (typeof onComplete === 'function') onComplete()
	}

	if (!ANALYTICS_ENABLED || !isBrowser() || typeof window.gtag !== 'function') {
		finish()
		return
	}

	window.gtag('event', 'add_to_slack_click', {
		event_category: 'marketing',
		event_label: location,
		event_callback: finish,
	})

	setTimeout(finish, 500)
}

/**
 * Returns props to spread onto an `<a>` (or `<button>`-as-link) so that a
 * click first records the `add_to_slack_click` event and only then navigates
 * to `slackAuthUrl`. Without this the navigation would routinely cancel the
 * gtag beacon and the event would be undercounted.
 *
 * Only intercepts plain left-clicks; cmd/ctrl/shift/middle clicks are left
 * to the browser so users can still open the auth URL in a new tab.
 */
export const buildAddToSlackClickHandler = (slackAuthUrl, location) => ({
	onClick: event => {
		if (event.defaultPrevented) return
		if (event.button !== 0) return
		if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

		event.preventDefault()
		trackAddToSlackClick(location, () => {
			window.location.href = slackAuthUrl
		})
	},
})
